import { signal } from "@preact/signals-react";

import type { SyncEnvelope } from "@/shared/api/sync";
import { onSyncMessage, onSyncOpen, publishSyncMessage, syncConnectionState } from "@/shared/api/sync";

export type BedLock = {
    bed_id: string;
    user_id: string;
    user_name: string;
    locked_at: string;
};

type BedLockMap = Record<string, BedLock>;

type BedLockRequest = {
    bedID: string;
    userID: string;
    userName: string;
};

const lockTTLMS = 5 * 60 * 1000;

export const bedLocks = signal<BedLockMap>({});

let initialized = false;

export function initBedLockSync() {
    if (initialized) {
        return;
    }

    initialized = true;
    onSyncMessage(handleSyncMessage);
    onSyncOpen(requestBedLockSync);

    if (syncConnectionState.value === "connected") {
        requestBedLockSync();
    }
}

export function resetBedLocks() {
    bedLocks.value = {};
}

export function getBedLock(bedID: string) {
    return bedLocks.value[bedID] ?? null;
}

export function isBedLockedByOther(bedID: string, currentUserID: string) {
    const lock = getBedLock(bedID);
    return !!lock && lock.user_id !== currentUserID;
}

export function setBedLocks(nextLocks: BedLockMap) {
    bedLocks.value = pruneExpiredLocks(nextLocks);
}

export function upsertBedLock(lock: BedLock) {
    bedLocks.value = pruneExpiredLocks({ ...bedLocks.value, [lock.bed_id]: lock });
}

export function removeBedLock(bedID: string) {
    if (!(bedID in bedLocks.value)) {
        return;
    }

    const next = { ...bedLocks.value };
    delete next[bedID];
    bedLocks.value = next;
}

export function acquireBedLock(request: BedLockRequest) {
    const lock = {
        bed_id: request.bedID,
        user_id: request.userID,
        user_name: request.userName,
        locked_at: new Date().toISOString(),
    } satisfies BedLock;

    upsertBedLock(lock);
    if (!publishSyncMessage("bed.lock.acquire", { bed_id: request.bedID })) {
        removeBedLock(request.bedID);
        return false;
    }

    return true;
}

export function releaseBedLock(request: Pick<BedLockRequest, "bedID">) {
    removeBedLock(request.bedID);
    return publishSyncMessage("bed.lock.release", { bed_id: request.bedID });
}

export function heartbeatBedLock(request: Pick<BedLockRequest, "bedID">) {
    return publishSyncMessage("bed.lock.heartbeat", { bed_id: request.bedID });
}

function handleSyncMessage(message: SyncEnvelope) {
    const type = String(message.type ?? "").trim();
    switch (type) {
        case "bed.lock.sync": {
            const payload = message.data as Partial<{ locks: BedLockMap }> | null | undefined;
            setBedLocks(payload?.locks ?? {});
            break;
        }
        case "bed.lock.updated": {
            const payload = message.data as Partial<BedLock> | null | undefined;
            if (payload?.bed_id && payload.user_id && payload.user_name && payload.locked_at) {
                upsertBedLock(payload as BedLock);
            }
            break;
        }
        case "bed.lock.released": {
            const payload = message.data as Partial<{ bed_id: string }> | null | undefined;
            if (payload?.bed_id) {
                removeBedLock(payload.bed_id);
            }
            break;
        }
        default:
            break;
    }
}

function requestBedLockSync() {
    void publishSyncMessage("bed.lock.sync.request", {});
}

function pruneExpiredLocks(locks: BedLockMap) {
    const now = Date.now();
    const next: BedLockMap = {};

    for (const [bedID, lock] of Object.entries(locks)) {
        const lockedAt = new Date(lock.locked_at).getTime();
        if (!Number.isFinite(lockedAt) || now - lockedAt > lockTTLMS) {
            continue;
        }

        next[bedID] = lock;
    }

    return next;
}
